import React, { useCallback, useEffect, useState } from "react";
import { ModeBadge } from "./ModeBadge";
import axios from "axios";
import { toast } from "sonner";
import { Crosshair, TrendingUp, TrendingDown, Volume2, Zap, Clock, RefreshCw, ChevronDown, ChevronRight, Download, Power, ShieldOff } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtNum = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

// 2026-10-09 — log-only ±2% day-change band test (backend sniper_band_shadow.py).
const BandShadowCard = () => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/sniper-autopilot/band-shadow-summary`, { params: { days: 30 } })
      .then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return null;
  const f = (v, s = "") => (v == null ? "—" : `${v}${s}`);
  const Row = ({ label, st }) => (
    <tr className="border-b border-grid/40">
      <td className="px-3 py-1.5 text-muted-2">{label}</td>
      <td className="px-3 py-1.5">{f(st?.trades)}</td>
      <td className="px-3 py-1.5">{f(st?.days_with_pick)}</td>
      <td className="px-3 py-1.5">{f(st?.win_rate_pct, "%")}</td>
      <td className="px-3 py-1.5" style={{ color: (st?.avg_pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
        {st?.avg_pnl_pct == null ? "—" : `${st.avg_pnl_pct >= 0 ? "+" : ""}${st.avg_pnl_pct}%`}
      </td>
      <td className="px-3 py-1.5">{f(st?.profit_factor)}</td>
    </tr>
  );
  return (
    <div className="border-grid surface px-5 py-3 space-y-2" data-testid="sniper-band-shadow-card">
      <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
        Band test · extra picks with a ±{d.band_pct ?? 2}% day-change band (last 30 days, log only)
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Picks that fail only the live day-change band but pass at ±{d.band_pct ?? 2}%, tracked on the real option price
        with the same target/stop and 15:15 exit. No orders. Widen the live band only if these hold up over ~30 picks.
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
          <tr className="border-b border-grid">
            {["", "Closed", "Days with pick", "Win rate", "Avg / trade", "Profit factor"].map((h) => (
              <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <Row label="Real Sniper trades" st={d.real_sniper} />
          <Row label={`Extra ±${d.band_pct ?? 2}% picks`} st={d.shadow} />
        </tbody>
      </table>
      {d.shadow?.open ? <div className="text-[10px] text-muted-3 font-mono-data">{d.shadow.open} open now.</div> : null}
      {(d.rows || []).length > 0 && (
        <>
          <button className="text-[10px] underline text-muted-2 font-mono-data" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show"} picks
          </button>
          {open && (
            <table className="w-full text-[11px] font-mono-data">
              <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
                <tr className="border-b border-grid">
                  {["Day", "Time", "Symbol", "Dir", "Day chg", "Option", "Entry → exit", "P&L", "Exit"].map((h) => (
                    <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r, i) => (
                  <tr key={i} className="border-b border-grid/40">
                    <td className="px-3 py-1.5">{r.day}</td>
                    <td className="px-3 py-1.5">{r.time}</td>
                    <td className="px-3 py-1.5 font-semibold">{r.symbol}</td>
                    <td className="px-3 py-1.5">{r.direction}</td>
                    <td className="px-3 py-1.5">{f(r.change_pct, "%")}</td>
                    <td className="px-3 py-1.5">{r.option}</td>
                    <td className="px-3 py-1.5">{fmtNum(r.entry)} → {r.exit == null ? "…" : fmtNum(r.exit)}</td>
                    <td className="px-3 py-1.5"><PnlPill value={r.pnl_pct} /></td>
                    <td className="px-3 py-1.5 text-muted-2">{r.status === "OPEN" ? "open" : r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

// 2026-10-13 — log-only anti-chase check (backend sniper_chase_shadow.py).
const ChaseShadowCard = () => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/sniper-autopilot/chase-shadow-summary`, { params: { days: 30 } })
      .then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return null;
  const f = (v, s = "") => (v == null ? "—" : `${v}${s}`);
  const th = d.thresholds || {};
  const Row = ({ label, st }) => (
    <tr className="border-b border-grid/40">
      <td className="px-3 py-1.5 text-muted-2">{label}</td>
      <td className="px-3 py-1.5">{f(st?.closed)}</td>
      <td className="px-3 py-1.5">{f(st?.win_rate_pct, "%")}</td>
      <td className="px-3 py-1.5" style={{ color: (st?.avg_pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
        {st?.avg_pnl_pct == null ? "—" : `${st.avg_pnl_pct >= 0 ? "+" : ""}${st.avg_pnl_pct}%`}
      </td>
      <td className="px-3 py-1.5">{f(st?.sum_pnl_pct, "%")}</td>
      <td className="px-3 py-1.5">{f(st?.profit_factor)}</td>
    </tr>
  );
  return (
    <div className="border-grid surface px-5 py-3 space-y-2" data-testid="sniper-chase-shadow-card">
      <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
        Anti-chase · entries after a sharp run-up (last 30 days)
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Flags an entry if the premium rose ≥{th.chase_shadow_opt_3m_pct ?? 8}% in 3 min or ≥{th.chase_shadow_opt_5m_pct ?? 10}% in 5 min,
        the stock moved ≥{th.chase_shadow_stock_atr ?? 1} ATR in 5 min, or RVOL ≥{th.chase_shadow_rvol ?? 4}.
        Since 28-Oct the premium flags block the LIVE buy (paper still recorded); the stock-ATR and RVOL flags are log only.
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
          <tr className="border-b border-grid">
            {["", "Closed", "Win rate", "Avg / trade", "Sum", "Profit factor"].map((h) => (
              <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <Row label="Would be blocked" st={d.would_block} />
          <Row label="Kept" st={d.kept} />
          <Row label="· premium 3m" st={d.by_reason?.premium_3m} />
          <Row label="· premium 5m" st={d.by_reason?.premium_5m} />
          <Row label="· stock ATR" st={d.by_reason?.stock_atr} />
          <Row label="· RVOL" st={d.by_reason?.rvol} />
        </tbody>
      </table>
      {(d.trades || []).length > 0 && (
        <>
          <button className="text-[10px] underline text-muted-2 font-mono-data" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show"} trades
          </button>
          {open && (
            <table className="w-full text-[11px] font-mono-data">
              <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
                <tr className="border-b border-grid">
                  {["Day", "Symbol", "Live", "Prem 3m", "Prem 5m", "From 5m low", "Stock ATR", "RVOL", "Flag", "P&L", "Exit"].map((h) => (
                    <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.trades.map((r, i) => (
                  <tr key={i} className="border-b border-grid/40">
                    <td className="px-3 py-1.5">{r.day}</td>
                    <td className="px-3 py-1.5 font-semibold">{r.symbol}</td>
                    <td className="px-3 py-1.5">{r.live ? "live" : "paper"}</td>
                    <td className="px-3 py-1.5">{f(r.opt_rise_3m_pct, "%")}</td>
                    <td className="px-3 py-1.5">{f(r.opt_rise_5m_pct, "%")}</td>
                    <td className="px-3 py-1.5">{f(r.opt_rise_from_low_5m_pct, "%")}</td>
                    <td className="px-3 py-1.5">{f(r.stock_move_5m_atr)}</td>
                    <td className="px-3 py-1.5">{f(r.rvol)}</td>
                    <td className="px-3 py-1.5" style={{ color: r.would_block ? "var(--bear)" : undefined }}>
                      {r.would_block ? (r.reasons || []).join(", ") : "—"}
                    </td>
                    <td className="px-3 py-1.5"><PnlPill value={r.pnl_pct} /></td>
                    <td className="px-3 py-1.5 text-muted-2">{r.status === "OPEN" ? "open" : r.close_reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

const PnlPill = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const pos = value >= 0;
  return (
    <span className="font-mono-data" style={{ color: pos ? "var(--bull)" : "var(--bear)" }}>
      {pos ? "+" : ""}
      {fmtNum(value)}%
    </span>
  );
};

const fmtISTDateTime = (iso) => {
  if (!iso) return "—";
  try {
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(s)).replace(",", "");
  } catch {
    return "—";
  }
};

const DetailStat = ({ label, value, color }) => (
  <span>
    {label}{" "}
    <span className="font-semibold" style={color ? { color } : undefined}>
      {value}
    </span>
  </span>
);

const SniperTradeRow = ({ t, i, groupTestid, onManualExit }) => {
  const [expanded, setExpanded] = useState(false);
  const live = t.current_ce_ltp || t.close_ce_ltp;
  const pnl = t.final_pnl_pct ?? t.pnl_pct;
  const isClosed = t.status === "CLOSED";
  let statusLabel;
  if (!isClosed) statusLabel = "OPEN";
  else if (t.close_reason === "PROFIT_BOOKED") statusLabel = "BOOKED";
  else if (t.close_reason === "STOP_LOSS") statusLabel = "STOP LOSS";
  else if (t.close_reason === "MANUAL_EXIT") statusLabel = "MANUAL EXIT";
  else if (t.close_reason === "EOD") statusLabel = "EOD CLOSE";
  else statusLabel = t.close_reason || "CLOSED";
  const statusColor = !isClosed
    ? "var(--bull)"
    : statusLabel === "BOOKED"
    ? "var(--bull)"
    : statusLabel === "STOP LOSS"
    ? "var(--bear)"
    : "var(--text-muted)";
  const rvolColor = t.rvol == null ? "var(--text-muted)" : t.rvol >= 1.5 ? "var(--bull)" : "var(--text-muted-2)";
  const changeColor = (t.change_pct_at_entry ?? 0) >= 0 ? "var(--bull)" : "var(--bear)";
  return (
    <>
      <tr
        data-testid={`${groupTestid}-row-${i}`}
        className="border-b border-grid hover:bg-white/[0.02] cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-2 text-muted-2 font-mono-data">
          <span className="inline-flex items-center gap-1.5">
            <span className="text-muted-3">
              {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            </span>
            {fmtISTDateTime(t.entered_at)}
          </span>
        </td>
        <td className="px-4 py-2 font-semibold">
          {t.symbol}
          <span className="ml-2 text-[9px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
            {t.setup_direction}
          </span>
          {/* 2026-09-18 — real Upstox order vs. a simulated pick, at a
              glance (same intent as Commodities' "Real fill" badge). */}
          <ModeBadge t={t} />
          {/* 2026-10-12 — chat: "Sniper Setup Live trading is on...still no order placed".
              Why a pick stayed paper was stored (live_skip_reason) but never shown. */}
          {!t.live_trade && t.live_skip_reason && t.live_skip_reason !== "live_trading_off" && (
            <div className="text-[9px] font-mono-data font-normal mt-0.5" style={{ color: "var(--bear)" }}
                 title="Why no live order was placed for this pick">
              paper · {t.live_skip_reason}{t.live_error_message ? ` — ${t.live_error_message}` : ""}
            </div>
          )}
        </td>
        <td className="px-4 py-2 text-right font-mono-data">
          {t.atm_strike} {t.option_type}
        </td>
        <td className="px-4 py-2 text-right font-mono-data">₹{fmtNum(t.atm_ce_ltp)}</td>
        <td className="px-4 py-2 text-right font-mono-data">{live ? `₹${fmtNum(live)}` : "—"}</td>
        <td className="px-4 py-2 text-right">
          <PnlPill value={pnl} />
        </td>
        <td className="px-4 py-2 text-right font-mono-data">{fmtNum(t.score_at_entry, 0)}</td>
        <td className="px-4 py-2 text-right font-mono-data" style={{ color: rvolColor }}>
          {t.rvol == null ? "—" : `${fmtNum(t.rvol, 2)}×`}
        </td>
        <td className="px-4 py-2 text-right font-mono-data">{t.rsi == null ? "—" : fmtNum(t.rsi, 1)}</td>
        <td className="px-4 py-2 text-right">
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border"
            style={{ color: statusColor, borderColor: statusColor === "var(--text-muted)" ? "var(--border-color)" : statusColor }}
          >
            {statusLabel}
          </span>
        </td>
        <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
          {!isClosed && onManualExit && (
            <button
              onClick={() => onManualExit(t.id)}
              data-testid={`${groupTestid}-exit-${i}`}
              className="text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border hover:border-white/60"
              style={{ color: "var(--text-muted)" }}
              title="Close this paper trade now at the current LTP"
            >
              Exit
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-grid" style={{ background: "rgba(255,255,255,0.015)" }}>
          <td colSpan={11} className="px-4 py-2.5">
            <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono-data text-[11px] text-muted-2 pl-6">
              <DetailStat label="Day Δ" value={t.change_pct_at_entry == null ? "—" : `${t.change_pct_at_entry >= 0 ? "+" : ""}${fmtNum(t.change_pct_at_entry, 2)}%`} color={changeColor} />
              <DetailStat label="R:R" value={t.risk_reward_ratio == null ? "—" : `1:${fmtNum(t.risk_reward_ratio, 2)}`} />
              <DetailStat label="Trend" value={t.trend || "—"} />
              <DetailStat label="VWAP" value={t.vwap == null ? "—" : fmtNum(t.vwap)} />
              <DetailStat label="EMA9/21" value={t.ema9 == null || t.ema21 == null ? "—" : `${fmtNum(t.ema9)} / ${fmtNum(t.ema21)}`} />
              <DetailStat label="MACD Hist" value={t.macd_hist == null ? "—" : fmtNum(t.macd_hist, 4)} />
              <DetailStat label="Volume" value={t.volume == null ? "—" : Number(t.volume).toLocaleString("en-IN")} />
              <DetailStat label="Vol Avg(20)" value={t.vol_avg == null ? "—" : Number(Math.round(t.vol_avg)).toLocaleString("en-IN")} />
              <DetailStat label="Vol Spike" value={t.vol_spike ? "YES" : "no"} color={t.vol_spike ? "var(--bull)" : undefined} />
              <DetailStat label="Breakout" value={t.breakout_up ? "UP" : t.breakout_down ? "DOWN" : "no"} color={(t.breakout_up || t.breakout_down) ? "var(--bull)" : undefined} />
              <DetailStat label="ATR" value={t.atr == null ? "—" : fmtNum(t.atr)} />
              <DetailStat label="IV" value={t.iv_at_entry == null ? "—" : `${fmtNum(t.iv_at_entry, 1)}%`} />
              <DetailStat label="Delta" value={t.delta_at_entry == null ? "—" : fmtNum(t.delta_at_entry, 3)} />
              <DetailStat label="ADX" value={t.adx_at_entry == null ? "—" : fmtNum(t.adx_at_entry, 1)} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const SniperPaperHistory = ({ trades, onManualExit, liveEnabled, liveBusy, onToggleLive, onRefresh, refreshing }) => {
  const today = trades?.today || [];
  const earlier = trades?.earlier || [];
  const total = today.length + earlier.length;
  const [open, setOpen] = useState(false);

  const groups = [
    { label: "Today", items: today, testid: "sniper-history-today" },
    { label: "Earlier", items: earlier, testid: "sniper-history-earlier" },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="border-grid surface" data-testid="sniper-autopilot-history">
      <div className="w-full px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
        <button
          data-testid="sniper-history-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 flex items-center justify-between text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/[0.02]"
        >
          <span>Auto Paper Trades · last {trades?.retention_days ?? 14} days · {total} trade(s)</span>
          <span style={{ color: "var(--text-muted)" }}>{open ? "Hide" : "Show"}</span>
        </button>
        {/* 2026-09-18 — Live/Pause kill-switch toggle (chat: "Go ahead with
            phased approach and enable Live Trading for Sniper Setup").
            Lives here (not a separate settings page) since this whole panel
            is already admin-only — same button treatment as Candle
            Breakout's live-toggle. */}
        {onToggleLive && (
          <button
            data-testid="sniper-live-toggle-btn"
            onClick={(e) => { e.stopPropagation(); onToggleLive(); }}
            disabled={liveBusy}
            className="border-grid px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{
              color: liveEnabled ? "var(--bull)" : "var(--bear)",
              borderColor: liveEnabled ? "var(--bull)" : "var(--bear)",
              background: liveEnabled ? "var(--bull-muted)" : "var(--surface)",
            }}
            title={liveEnabled
              ? "LIVE trading ACTIVE — click to pause new buys"
              : "LIVE trading PAUSED — click to resume new buys"}
          >
            {liveEnabled ? <Power size={12} /> : <ShieldOff size={12} />}
            {liveEnabled ? "Live · ON" : "Live · OFF"}
          </button>
        )}
        {/* 2026-09-23 (chat: "instead of auto-refresh...keep Refresh
            button...that might reduce upstox calls") — this table used to
            poll every 60s regardless of whether anyone had the page open
            (see SniperAutopilotPanel below); now loads once and only again
            on demand, same manual-refresh convention as every scan section
            on this page. */}
        {onRefresh && (
          <button
            data-testid="sniper-history-refresh"
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-2 py-1 border border-grid text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        )}
        <a
          href={`${API}/sniper-autopilot/export.csv`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sniper-export-csv"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 px-2 py-1 border border-grid text-[10px] uppercase tracking-wider font-mono-data hover:text-white"
          title="Export all closed trades (full data) as CSV"
        >
          <Download size={12} /> Export CSV
        </a>
      </div>
      {open && (
        <div>
          {groups.length === 0 ? (
            <div className="px-4 py-4 text-xs text-muted-3">
              No auto-entries yet. Runs every 5 min, 09:30-14:00 IST — Score ≥80, R:R ≥1:2.0,
              day-change within ±1%, RVOL ≥1.5×.
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.label}>
                <div className="px-4 py-1.5 border-y border-grid text-[10px] uppercase tracking-wider font-mono-data text-muted-3">
                  {g.label} ({g.items.length})
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                      <th className="text-left px-4 py-2">Entry IST</th>
                      <th className="text-left px-4 py-2">Symbol</th>
                      <th className="text-right px-4 py-2">Strike</th>
                      <th className="text-right px-4 py-2">Entry Premium</th>
                      <th className="text-right px-4 py-2">Close/Curr</th>
                      <th className="text-right px-4 py-2">P&amp;L</th>
                      <th className="text-right px-4 py-2">Score</th>
                      <th className="text-right px-4 py-2">RVOL</th>
                      <th className="text-right px-4 py-2">RSI</th>
                      <th className="text-right px-4 py-2">Status</th>
                      <th className="text-right px-4 py-2 w-20">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((t, i) => (
                      <SniperTradeRow
                        key={t.id || i}
                        t={t}
                        i={i}
                        groupTestid={g.testid}
                        onManualExit={onManualExit}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const SniperAutopilotPanel = () => {
  const [trades, setTrades] = useState(null);
  const [liveEnabled, setLiveEnabled] = useState(false);
  const [liveBusy, setLiveBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [latestRun, setLatestRun] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const [tradesRes, liveRes, todayRes] = await Promise.all([
        axios.get(`${API}/sniper-autopilot/paper-trades`),
        axios.get(`${API}/sniper-autopilot/live-status`).catch(() => ({ data: { enabled: false } })),
        axios.get(`${API}/sniper-autopilot`).catch(() => ({ data: null })),
      ]);
      setTrades(tradesRes.data);
      setLiveEnabled(!!liveRes.data?.enabled);
      setLiveStatus(liveRes.data || null);
      setLatestRun((todayRes.data?.runs || [])[0] || null);
    } catch (e) {
      // Silent — this panel is secondary to the main Sniper cards above.
    } finally {
      if (manual) setRefreshing(false);
    }
  }, []);

  // 2026-09-23 — briefly removed in favor of manual-only (chat: "instead
  // of auto-refresh...keep Refresh button...that might reduce upstox
  // calls"), then restored specifically for THIS table only (chat:
  // "let's rollback for this table...as I am enabling Live here") — once
  // real orders are live, this panel is the one place that shows fill
  // status/live P&L, so it needs to stay current without a manual click.
  // Unlike the OI Suggestions / Sniper scan-card removals (which DO poll
  // Upstox), this panel's two calls (paper-trades/live-status) are DB
  // reads only, so restoring it doesn't add back any Upstox/Cloudflare
  // rate pressure — that's exactly why it's safe to special-case back in
  // here while every other section stays manual-only. The Refresh button
  // added alongside this stays too, for an immediate check right after a
  // fill.
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const onManualExit = useCallback(
    async (tradeId) => {
      try {
        await axios.post(`${API}/sniper-autopilot/manual-exit`, { trade_id: tradeId });
        toast.success("Trade closed");
        await load();
      } catch (e) {
        toast.error("Manual exit failed");
      }
    },
    [load]
  );

  const onToggleLive = useCallback(async () => {
    const next = !liveEnabled;
    const verb = next ? "RESUME" : "PAUSE";
    if (!window.confirm(
      `${verb} LIVE trading for Sniper Setup?\n\n` +
      (next
        ? "New auto-entries will place real Upstox BUY orders again."
        : "New auto-entries will be SKIPPED (no new live BUYs).\n" +
          "Existing OPEN live positions will still be SOLD on TP/SL/EOD/manual-exit.")
    )) return;
    setLiveBusy(true);
    try {
      await axios.post(`${API}/sniper-autopilot/live-status`, { enabled: next });
      setLiveEnabled(next);
      if (next) setLiveStatus(null);
      toast.success(next ? "LIVE trading RESUMED" : "LIVE trading PAUSED — new buys blocked");
    } catch (e) {
      toast.error("Failed to update live-trading status");
    } finally {
      setLiveBusy(false);
    }
  }, [liveEnabled]);

  const paused = !liveEnabled && liveStatus?.paused_by === "live_guard" ? liveStatus : null;
  const pi = paused?.paused_info || {};
  const pausedWhy = {
    consecutive_losses: `${pi.loss_streak} losing live trades in a row`,
    profit_factor_below_min: `live profit factor ${pi.pf} after ${pi.live_trades} trades`,
    order_problem: `order problem${pi.last_order_problem ? ` (${pi.last_order_problem.symbol}: ${pi.last_order_problem.live_error || pi.last_order_problem.live_sell_error})` : ""}`,
  }[paused?.paused_reason] || paused?.paused_reason;

  return (
    <>
      {paused && (
        <div
          className="border-l-4 px-4 py-2 text-xs"
          data-testid="sniper-live-guard-paused"
          style={{ borderLeftColor: "var(--bear)", background: "rgba(228, 90, 90, 0.08)" }}
        >
          <strong style={{ color: "var(--bear)" }}>Sniper LIVE paused by the live guard</strong> — {pausedWhy}.
          {" "}Since live was switched on: {pi.live_trades ?? 0} live trades, net ₹
          {Math.round(pi.net_rs || 0).toLocaleString("en-IN")}. Paper trading continues. Review, then
          resume with the LIVE toggle (that starts a fresh record).
        </div>
      )}
      {latestRun && (
        <div className="border-grid surface px-5 py-3" data-testid="sniper-autopilot-latest-run">
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            Latest auto-scan · {latestRun.ran_at_ist || latestRun.slot} ·{" "}
            {latestRun.qualifying_count ?? 0} qualifying · {(latestRun.entered || []).length} entered
            {latestRun.scan_stats?.duration_sec != null ? ` · ${latestRun.scan_stats.duration_sec}s` : ""}
          </div>
          {(latestRun.qualifying_count ?? 0) === 0 && (latestRun.why_no_picks || []).length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold text-muted-1">Why no auto-picks?</div>
              <ul className="text-xs text-muted-2 space-y-0.5 list-disc pl-5 mt-1">
                {latestRun.why_no_picks.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      <BandShadowCard />
      <ChaseShadowCard />
      <SniperPaperHistory
        trades={trades}
        onManualExit={onManualExit}
        liveEnabled={liveEnabled}
        liveBusy={liveBusy}
        onToggleLive={onToggleLive}
        onRefresh={() => load(true)}
        refreshing={refreshing}
      />
    </>
  );
};

const IndicatorBadge = ({ label, value, active, positive }) => {
  const color = active
    ? positive
      ? "var(--bull)"
      : "var(--bear)"
    : "var(--text-muted)";
  const bg = active
    ? positive
      ? "var(--bull-muted)"
      : "var(--bear-muted)"
    : "rgba(255,255,255,0.04)";
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] font-mono-data"
      style={{
        color,
        background: bg,
        borderColor: active ? color : "var(--border-color)",
      }}
    >
      <span className="uppercase tracking-wider opacity-70">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
};

const KeyValue = ({ label, value, color, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
      {label}
    </div>
    <div
      className="font-mono-data text-base font-semibold mt-0.5"
      style={color ? { color } : {}}
    >
      {value}
    </div>
  </div>
);

const SniperCard = ({ trade, idx }) => {
  const isBull = trade.direction === "BULLISH";
  const dirColor = isBull ? "var(--bull)" : "var(--bear)";
  const dirBg = isBull ? "var(--bull-muted)" : "var(--bear-muted)";
  const DirIcon = isBull ? TrendingUp : TrendingDown;

  return (
    <div
      data-testid={`sniper-trade-${idx + 1}`}
      className="border-grid surface flex flex-col hover:bg-white/[0.02] transition-colors"
    >
      {/* Card Header */}
      <div className="px-5 py-4 border-b border-grid flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center font-heading font-bold"
            style={{ background: dirBg, color: dirColor, border: `1px solid ${dirColor}` }}
          >
            <Crosshair size={16} />
          </div>
          <div>
            <div className="font-heading text-base font-semibold leading-tight">
              {trade.name}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              {trade.symbol.replace(".NS", "")} · NSE
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-mono-data border"
            style={{ color: dirColor, background: dirBg, borderColor: dirColor }}
          >
            <DirIcon size={11} />
            {trade.direction}
          </div>
          {trade.price != null && (
            <div
              data-testid={`sniper-price-${idx + 1}`}
              className="text-[10px] font-mono-data"
              style={{ color: (trade.change_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}
            >
              ₹{trade.price.toFixed(2)} ({(trade.change_pct ?? 0) >= 0 ? "+" : ""}
              {(trade.change_pct ?? 0).toFixed(2)}%)
            </div>
          )}
        </div>
      </div>

      {/* Indicators row */}
      <div className="px-5 py-3 border-b border-grid flex flex-wrap gap-1.5">
        <IndicatorBadge
          label="VWAP"
          value={trade.vwap}
          active
          positive={trade.price >= trade.vwap}
        />
        <IndicatorBadge
          label="EMA9/21"
          value={trade.ema9 > trade.ema21 ? "↑" : "↓"}
          active
          positive={trade.ema9 > trade.ema21}
        />
        <IndicatorBadge
          label="RSI"
          value={trade.rsi}
          active={trade.rsi > 55 || trade.rsi < 45}
          positive={trade.rsi > 50}
        />
        <IndicatorBadge
          label="MACD"
          value={trade.macd_hist > 0 ? "↑" : "↓"}
          active
          positive={trade.macd_hist > 0}
        />
        {trade.vol_spike && (
          <IndicatorBadge label="VOL" value="SPIKE" active positive />
        )}
        {trade.breakout && (
          <IndicatorBadge label="BRK" value="OUT" active positive={isBull} />
        )}
      </div>

      {/* Levels */}
      <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-grid">
        <KeyValue
          testid={`sniper-entry-${idx + 1}`}
          label="Entry"
          value={`₹${trade.entry.toFixed(2)}`}
        />
        <KeyValue
          testid={`sniper-sl-${idx + 1}`}
          label="Stop Loss"
          value={`₹${trade.stop_loss.toFixed(2)}`}
          color="var(--bear)"
        />
        <KeyValue
          testid={`sniper-target-${idx + 1}`}
          label="Target"
          value={`₹${trade.target.toFixed(2)}`}
          color="var(--bull)"
        />
      </div>

      {/* Option strike */}
      <div className="px-5 py-4 border-b border-grid bg-white/[0.015]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap size={12} style={{ color: "var(--warn)" }} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
              Option Strike{trade.option.expiry ? ` · ${trade.option.expiry}` : ""}
            </div>
          </div>
          <span
            className="text-[9px] uppercase tracking-wider font-mono-data px-1.5 py-0.5 border"
            style={{
              color: (trade.option.source === "upstox" || trade.option.source === "nse") ? "var(--bull)" : "var(--text-muted)",
              borderColor:
                (trade.option.source === "upstox" || trade.option.source === "nse")
                  ? "var(--bull)"
                  : "var(--border-color)",
            }}
          >
            {trade.option.source === "upstox" ? "UPSTOX LIVE" : trade.option.source === "nse" ? "NSE LIVE" : "EST"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              ATM {trade.option.type}
            </div>
            <div className="font-mono-data text-base font-semibold">
              {trade.option.atm_strike}
            </div>
            {trade.option.atm && (
              <div className="text-[10px] font-mono-data text-muted-2 mt-1 space-y-0.5">
                {trade.option.atm.ltp != null && (
                  <div>LTP ₹{trade.option.atm.ltp}</div>
                )}
                {trade.option.atm.oi != null && (
                  <div>OI {Number(trade.option.atm.oi).toLocaleString("en-IN")}</div>
                )}
                {trade.option.atm.iv != null && (
                  <div>IV {Number(trade.option.atm.iv).toFixed(2)}%</div>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              ITM {trade.option.type}
            </div>
            <div className="font-mono-data text-base font-semibold">
              {trade.option.itm_strike}
            </div>
            {trade.option.itm && (
              <div className="text-[10px] font-mono-data text-muted-2 mt-1 space-y-0.5">
                {trade.option.itm.ltp != null && (
                  <div>LTP ₹{trade.option.itm.ltp}</div>
                )}
                {trade.option.itm.oi != null && (
                  <div>OI {Number(trade.option.itm.oi).toLocaleString("en-IN")}</div>
                )}
                {trade.option.itm.iv != null && (
                  <div>IV {Number(trade.option.itm.iv).toFixed(2)}%</div>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-2 mt-2">{trade.option.recommendation}</p>
      </div>

      {/* Reasoning + Score */}
      <div className="px-5 py-4 flex-1 flex flex-col">
        <p className="text-xs text-muted-2 leading-relaxed flex-1">
          {trade.reasoning}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-grid">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              R:R
            </div>
            <div className="font-mono-data text-sm font-semibold">
              1:{trade.risk_reward.toFixed(1)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              Score
            </div>
            <div
              className="font-mono-data text-sm font-bold"
              style={{ color: dirColor }}
            >
              {trade.score}/100
            </div>
          </div>
        </div>
      </div>

      {/* Scan Time footer (IST) */}
      {trade.scan_time_ist && (
        <div
          data-testid={`sniper-trade-scan-time-${idx + 1}`}
          className="px-5 py-2 border-t border-grid flex items-center gap-1.5 text-[10px] font-mono-data text-muted-3"
          style={{ background: "rgba(255,255,255,0.015)" }}
        >
          <Clock size={10} />
          <span className="uppercase tracking-wider">Scan Time</span>
          <span className="ml-auto" style={{ color: "var(--text-primary)" }}>
            {trade.scan_time_ist}
          </span>
        </div>
      )}
    </div>
  );
};

export const SniperTrades = ({ data, loading, onRefresh, refreshing, isAdmin }) => {
  const trades = data?.trades || [];
  const [collapsed, toggle] = useCollapsed("sniper-trades");
  return (
    <section data-testid="sniper-trades-section" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-muted-3 font-mono-data uppercase">
            04 / Sniper Setups
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-bold mt-1">
            High-Conviction Trade Setups
          </h2>
          <p className="text-xs text-muted-3 mt-1">
            Nifty 100 stocks · VWAP + EMA trend · RSI/MACD momentum · Volume
            breakout · Click Refresh for the latest scan · Auto paper-trades
            Score ≥80 / R:R ≥1:2.0 / day-change ±1% / RVOL ≥1.5× every 5 min (paper only)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            <Volume2 size={12} />
            Scanned: {data?.scanned ?? "—"}
            {data?.scan_stats?.universe ? ` / ${data.scan_stats.universe}` : ""}
            {data?.scan_source ? ` · ${data.scan_source}` : ""}
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="sniper-trades-refresh-btn"
              title="Re-scan now (bypasses cache)"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-grid text-[10px] uppercase tracking-[0.2em] font-mono-data hover:border-white/60 transition-colors disabled:opacity-40"
            >
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          )}
          <CollapseToggle
            collapsed={collapsed}
            onToggle={toggle}
            testId="sniper-trades-toggle"
          />
        </div>
      </div>

      {!collapsed && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {loading || !data
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border-grid surface p-5 space-y-3">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ))
          : trades.length > 0
          ? trades.map((t, idx) => <SniperCard key={t.symbol} trade={t} idx={idx} />)
          : (
            <div className="md:col-span-3 border-grid surface p-10 text-center">
              <Crosshair size={28} className="mx-auto mb-3 text-muted-3" />
              <h3 className="font-heading text-lg font-semibold">
                No qualified setups right now
              </h3>
              {(data?.why_no_picks || []).length > 0 ? (
                <div className="mt-3 text-left max-w-xl mx-auto" data-testid="sniper-why-no-picks">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data mb-1.5">
                    Why no picks?{data?.scan_source ? ` · ${data.scan_source}` : ""}
                  </div>
                  <ul className="text-xs text-muted-2 space-y-1 list-disc pl-5">
                    {data.why_no_picks.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-2 mt-2">
                  The market may be range-bound or scanning data is unavailable.
                  Auto-refresh in 5 min.
                </p>
              )}
            </div>
          )}
      </div>
      )}
      {/* 2026-09-18 — admin-gated (chat: "Need to implement admin gate for
          Sniper Setup's Paper trade and Live trading. Users can see Sniper
          Setup entries but not paper trade/Live trading"). The cards above
          (the manual-scan "entries") stay visible to every user — only the
          Auto Paper Trades panel (and, once built, Live Trading) is hidden
          here. The backend now enforces the same gate independently on
          every /sniper-autopilot/* route, so this isn't the only thing
          stopping a non-admin from reaching this data — it just avoids
          showing them an empty/403'ing panel. */}
      {!collapsed && isAdmin && <SniperAutopilotPanel />}
    </section>
  );
};